import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Lock, UserCheck, Globe, Mail, Bell, Database, Link } from 'lucide-react';

const PrivacyPolicy = () => {
  const { t } = useTranslation();

  const sections = [
    {
      icon: UserCheck,
      title: "Information Collection and Use",
      content: {
        intro: "We collect information to provide and improve our services. The types of personal data we may collect include:",
        subsections: [
          {
            subtitle: "Personal Information:",
            items: [
              "Name, company name, email address, phone number, physical address",
              "Information provided during account registration, including calendar data, meeting data, and related derivatives",
              "Information provided during transactions or purchases",
              "Information submitted via email or through our \"Contact\" section",
              "Information provided to subscribe to newsletters or updates"
            ]
          },
          {
            subtitle: "Usage Data:",
            items: [
              "IP address, browser type, browser version, pages visited, time and date of visits, time spent on pages, and diagnostic data"
            ]
          },
          {
            subtitle: "Tracking & Cookies Data:",
            items: [
              "Cookies and similar tracking technologies to monitor activity and store preferences. These include session cookies, preference cookies, and security cookies"
            ]
          }
        ]
      }
    },
    {
      icon: Database,
      title: "Use of Data",
      content: {
        items: [
          "To provide and maintain our Service",
          "To notify you about changes to our Service",
          "To provide customer care and support",
          "To monitor the usage of the Service",
          "To improve and personalize the Service",
          "To detect and prevent technical issues"
        ]
      }
    },
    {
      icon: Lock,
      title: "Data Sharing",
      content: {
        intro: "We do not share your personal data with third parties unless necessary to:",
        items: [
          "Comply with legal obligations",
          "Protect and defend the rights or property of Sonitrix",
          "Prevent or investigate wrongdoing in connection with the Service",
          "Protect the safety of users or the public"
        ]
      }
    },
    {
      icon: Shield,
      title: "Security of Data",
      content: {
        text: "The security of your data is important to us. We use commercially acceptable measures to protect your personal information. However, no method of transmission or storage is 100% secure."
      }
    },
    {
      icon: UserCheck,
      title: "User Rights",
      content: {
        intro: "Under applicable data protection laws (such as GDPR), you have rights to:",
        items: [
          "Access your data",
          "Correct inaccuracies in your data",
          "Request deletion of your data",
          "Withdraw consent for data processing",
          "Object to certain data uses"
        ],
        footer: "To exercise these rights, contact us at privacy@sonitrix.com"
      }
    },
    {
      icon: Globe,
      title: "International Data Transfers",
      content: {
        text: "Your data may be transferred to and processed in countries outside your location. By using our Service, you consent to such transfers in compliance with this Privacy Policy."
      }
    },
    {
      icon: Link,
      title: "Third-Party Links",
      content: {
        text: "Our Service may contain links to third-party websites. We are not responsible for the privacy practices of such sites and encourage you to read their privacy policies."
      }
    }
  ];

  return (
    <section className="py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header Section */}
        <div className="space-y-6 mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
            <Shield className="w-5 h-5 text-violet-600 mr-2" />
            <span className="text-sm font-medium text-violet-600">Privacy Policy</span>
          </div>
          
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          
          <p className="text-lg text-gray-600">
            Effective Date: {new Date().toLocaleDateString()}
          </p>

          <p className="text-gray-600">
            Sonitrix ("we," "us," or "our") operates the website sonitrix.com (the "Service"). 
            This Privacy Policy explains how we collect, use, and protect your information when you interact with our Service.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <section.icon className="w-5 h-5 text-violet-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">{section.title}</h2>
              </div>

              <div className="space-y-4">
                {section.content.intro && (
                  <p className="text-gray-600">{section.content.intro}</p>
                )}
                
                {section.content.text && (
                  <p className="text-gray-600">{section.content.text}</p>
                )}

                {section.content.subsections && section.content.subsections.map((subsection, idx) => (
                  <div key={idx} className="ml-4">
                    <h3 className="font-medium text-gray-900 mb-2">{subsection.subtitle}</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      {subsection.items.map((item, i) => (
                        <li key={i} className="text-gray-600">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}

                {section.content.items && (
                  <ul className="list-disc pl-5 space-y-2">
                    {section.content.items.map((item, idx) => (
                      <li key={idx} className="text-gray-600">{item}</li>
                    ))}
                  </ul>
                )}

                {section.content.footer && (
                  <p className="text-gray-600 mt-4">{section.content.footer}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 p-6 bg-white rounded-xl shadow-lg border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-violet-500/10">
              <Mail className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">Contact Us</h2>
          </div>
          <p className="text-gray-600">
            For questions about this Privacy Policy, please contact us at:
            <br />
            <strong>Sonitrix</strong>
            <br />
            <a href="mailto:privacy@sonitrix.com" className="text-violet-600 hover:text-violet-700">
              privacy@sonitrix.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
